import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { Document, Page } from 'react-pdf/dist/esm/entry.webpack';
import BlockUI from '../blockUi/BlockUi';

const PDFViewerV2 = (props) => {
    const pdfContainer = useRef();
    const viewerContainer = useRef();
    const [loading, setLoading] = useState(props.withLoader && !props.src);
    const [numberOfRenderedPages, setNumberOfRenderedPages] = useState([]);
    const [totalPages, setTotalPages] = useState(0);

    const intersectionObserverHandler = (entries, observer) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                observer.disconnect();
                setNumberOfRenderedPages(numberOfRenderedPages.concat([1]));
            }
        });
    };

    const handleDocumentOnLoad = (pdf) => {
        setTotalPages(pdf?._pdfInfo?.numPages);
        if (props.lazy) {
            setNumberOfRenderedPages([1]);
        } else {
            const pages = new Array(pdf?._pdfInfo?.numPages);
            pages.fill(1);
            setNumberOfRenderedPages(pages);
        }
    };

    useEffect(() => {
        if (props.src) {
            setLoading(false);
            setNumberOfRenderedPages([]);
            setTotalPages(0);
        }
    }, [props.src]);

    useEffect(() => {
        if (props.withLoader && props.fetchingReport) {
            setLoading(true);
        }
    }, [props.fetchingReport]);

    useEffect(() => {
        const domReady = !!viewerContainer.current && !!pdfContainer.current;
        if (props.lazy && domReady && totalPages > 1) {
            if (numberOfRenderedPages.length < totalPages) {
                const observer = new IntersectionObserver(intersectionObserverHandler, {
                    root: viewerContainer.current,
                    threshold: 0,
                });
                const lastPage = pdfContainer.current?.childNodes.item(0)
                    .childNodes.item(numberOfRenderedPages.length - 1);
                observer.observe(lastPage);
            }
        }
    }, [viewerContainer.current, pdfContainer.current, numberOfRenderedPages]);

    return (
        <div ref={viewerContainer}>
            <BlockUI
                parent="pdfviewerv2"
                containerProps={{ testId: 'pdf-loader' }}
                blocking={loading}
                renderChildren={!props.withLoader}
                backdropProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.3);' }
                }}
            >
                {
                    <div className="pdf-canvas-wrapper">
                        <div data-testid="pdf-data-id" ref={pdfContainer}>
                            <Document
                                file={props.base64 ? `data:application/pdf;base64,${props.src}` : props.src}
                                onLoadSuccess={handleDocumentOnLoad}
                                loading={''}
                            >
                                {
                                    numberOfRenderedPages.map((pages, index) => (<Page
                                        data-testid={`page-${index}`}
                                        key={`page-${index}`}
                                        pageIndex={index}
                                        scale={props.pdfScale}
                                        loading={props.withLoader ? <BlockUI blocking /> : null}
                                        renderTextLayer={false}
                                    />))
                                }
                            </Document>
                        </div>
                    </div>
                }
            </BlockUI>
        </div>
    );
};

PDFViewerV2.propTypes = {
    src: PropTypes.string,
    withLoader: PropTypes.bool,
    lazy: PropTypes.bool,
    fetchingReport: PropTypes.bool,
    base64: PropTypes.bool,
    pdfScale: PropTypes.number,
    containerOptions: PropTypes.shape({
        maxHeight: PropTypes.number
    }),
    fullPage: PropTypes.bool,
};

PDFViewerV2.defaultProps = {
    lazy: true,
    fullPage: false,
    pdfScale: 1.49,
    containerOptions: {
        maxHeight: 700,
    }
}

export default PDFViewerV2;
